import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function PollsTab({ event, user, isOrganizer }) {
  const [polls, setPolls] = useState([]);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/polls`);
      setPolls(r.data || []);
    } catch (err) {
      console.error('Failed to load polls:', err);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const cleanOpts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || cleanOpts.length < 2) return toast.error('Add a question and at least 2 options');
    try {
      await api.post(`/events/${event.id}/polls`, { question: question.trim(), options: cleanOpts });
      setQuestion(''); setOptions(['','']); setOpen(false); load(); toast.success('Poll created');
    } catch (err) {
      console.error('Failed to create poll:', err);
      toast.error(err?.response?.data?.detail || 'Failed to create poll');
    }
  };

  const vote = async (pollId, idx) => {
    try {
      await api.post(`/polls/${pollId}/vote`, { option_index: idx });
      load();
    } catch (err) {
      console.error('Failed to vote:', err);
      toast.error('Failed to vote');
    }
  };

  return (
    <div className="space-y-3" data-testid="polls-tab">
      <Card className="rounded-2xl p-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{polls.length} poll{polls.length === 1 ? '' : 's'}</div>
        <Button onClick={() => setOpen((v) => !v)} data-testid="poll-toggle-create" className="rounded-xl gradient-fab text-white"><Plus className="h-4 w-4 mr-1" /> {open ? 'Close' : 'New poll'}</Button>
      </Card>
      {open && (
        <Card className="rounded-2xl p-3 space-y-2" data-testid="poll-create-form">
          <Label>Question</Label>
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What’s for dinner?" data-testid="poll-question-input" />
          {options.map((o, i) => (
            <div key={`opt-${i}-row`} className="flex gap-2">
              <Input value={o} onChange={(e) => setOptions((arr) => arr.map((x, k) => k === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`} />
              {options.length > 2 && <Button type="button" variant="ghost" onClick={() => setOptions((arr) => arr.filter((_, k) => k !== i))} className="rounded-xl text-destructive"><X className="h-4 w-4" /></Button>}
            </div>
          ))}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setOptions((arr) => [...arr, ''])} data-testid="poll-add-option" className="rounded-xl">Add option</Button>
            <Button type="button" onClick={create} data-testid="poll-create-button" className="rounded-xl gradient-fab text-white ml-auto">Create</Button>
          </div>
        </Card>
      )}
      {polls.length === 0 && <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">No polls yet.</Card>}
      {polls.map((p) => (
        <Card key={p.id} className="rounded-2xl p-4" data-testid={`poll-${p.id}`}>
          <div className="font-semibold mb-3">{p.question}</div>
          <div className="space-y-2">
            {p.options.map((opt, i) => {
              const total = p.total_votes || 0;
              const c = (p.counts || [])[i] || 0;
              const pct = total ? Math.round((c / total) * 100) : 0;
              const mine = p.my_vote === i;
              return (
                <button key={`${p.id}-opt-${i}`} onClick={() => vote(p.id, i)} data-testid={`poll-${p.id}-opt-${i}`} className={`relative w-full text-left rounded-xl border ${mine ? 'border-primary' : 'border-border'} px-3 py-2 overflow-hidden`}>
                  <span className="absolute inset-y-0 left-0 bg-primary/10" style={{ width: `${pct}%` }} />
                  <span className="relative flex items-center justify-between text-sm">
                    <span className="font-medium">{opt}</span>
                    <span className="text-xs text-muted-foreground">{c} · {pct}%</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">{p.total_votes || 0} vote{(p.total_votes || 0) === 1 ? '' : 's'}</div>
        </Card>
      ))}
    </div>
  );
}
